import React, { useState, useEffect, useMemo } from 'react';
import Page from '../UI/Page';
import { 
  Box, 
  Heading, 
  Table, 
  Thead, 
  Tbody, 
  Tr, 
  Th, 
  Flex, 
  Tooltip, 
  Spinner, 
  Center, 
  Text,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  VStack,
  Card,
  CardBody,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  HStack,
} from "@chakra-ui/react";
import proxy from '../../proxy';
import axios from "axios";
import Stock from './Stock';
import DividendRow from './DividendRow';

const Stocks = () => {
  const [stocks, setStocks] = useState([]);
  const [dividends, setDividends] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // ✅ Fetch stocks (obligatoriu)
        const stocksRes = await axios.get(`${proxy}server_mts/GetStocks.php`, {
          headers: { 'Cache-Control': 'no-cache' },
        });

        if (!stocksRes.data || !Array.isArray(stocksRes.data)) {
          throw new Error("Invalid stock data received.");
        }

        setStocks(stocksRes.data);

        // ✅ Fetch dividends (opțional - nu oprește aplicația)
        try {
          const dividendsRes = await axios.get(`${proxy}server_mts/admin/GetAllDividends.php`, {
            headers: { 'Cache-Control': 'no-cache' },
          });
          setDividends(dividendsRes.data || []);
        } catch (divError) {
          console.warn("Dividends table not available:", divError.message);
          setDividends([]); // Array gol dacă nu există tabela
        }

      } catch (error) {
        console.error("Error fetching stocks:", error.message);
        setError("Unable to retrieve the stock data.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);
  
  const [currentPrices, setCurrentPrices] = useState({});

  const { totalDividends, totalPaidDividends, totalValue, totalInvested, totalProfit, totalCommissions, avgDividendYield } = useMemo(() => {
    const totalDividends = stocks.reduce((sum, stock) => {
      const yearly = stock.div_share * stock.div_per_year * stock.nr_shares;
      return sum + yearly;
    }, 0);

    const totalPaidDividends = stocks.reduce((sum, stock) => {
      return sum + Number(stock.paid_div || 0);
    }, 0);

    const totalInvested = stocks.reduce((sum, stock) => {
      return sum + (stock.average_price_share * stock.nr_shares);
    }, 0);

    const totalCommissions = stocks.reduce((sum, stock) => {
      return sum + Number(stock.commission || 0);
    }, 0);

    const totalValue = stocks.reduce((sum, stock) => {
      const currentPrice = currentPrices[stock.id] || stock.average_price_share;
      return sum + (currentPrice * stock.nr_shares);
    }, 0);

    const totalProfit = totalValue - totalInvested;
    const avgDividendYield = totalInvested > 0 ? ((totalDividends / totalInvested) * 100).toFixed(2) : 0;

    return { 
      totalDividends, 
      totalPaidDividends, 
      totalValue, 
      totalInvested, 
      totalProfit,
      totalCommissions,
      avgDividendYield
    };
  }, [stocks, currentPrices]);

  // ✅ Calculează din tabela dividends (dacă există)
  const totalDividendsReceived = dividends.reduce((sum, d) => sum + Number(d.amount || 0), 0);
  const totalTaxPaid = dividends.reduce((sum, d) => sum + Number(d.tax || 0), 0);
  const netDividends = totalDividendsReceived - totalTaxPaid;

  const totalProfitPercent = totalInvested > 0 ? ((totalProfit / totalInvested) * 100).toFixed(2) : 0;
  const recoveryPercent = totalInvested > 0 ? ((totalPaidDividends / (totalInvested + totalCommissions)) * 100).toFixed(2) : 0;

  const bestStock = stocks.reduce((best, stock) => {
    const profit = (stock.current_price - stock.average_price_share) / stock.average_price_share * 100;
    const bestProfit = (best.current_price - best.average_price_share) / best.average_price_share * 100;
    return profit > bestProfit ? stock : best;
  }, stocks[0]);

  if (isLoading) {
    return (
      <Page>
        <Center minH="70vh">
          <VStack spacing={4}>
            <Spinner size="xl" color="teal.300" thickness="4px" speed="0.65s" />
            <Text color="gray.400" fontSize="sm">Loading Mattias portfolio...</Text>
          </VStack>
        </Center>
      </Page>
    );
  }

  if (error) {
    return (
      <Page>
        <Center minH="70vh">
          <VStack spacing={4}>
            <Text fontSize="5xl">📉</Text>
            <Text color="red.400" fontSize="lg" fontWeight="semibold">
              {error}
            </Text>
            <Text color="gray.500" fontSize="sm">
              Please refresh the page or check your connection
            </Text>
          </VStack>
        </Center>
      </Page>
    );
  }

  return (
    <Page>
      <div className="hero" style={{ minHeight: '70vh' }}>
        <VStack spacing={6} p={{ base: 2, md: 4 }} mt={6}>
          
          {/* Header Section */}
          <VStack spacing={1} mb={4}>
            <Heading size="sm" color="teal.300" fontWeight="semibold">
              Mattias Assets Portfolio
            </Heading>
            <Text fontSize="xs" color="gray.500">📈 Investment Tracker</Text>
          </VStack>

          {/* Summary Statistics Cards */}
          <SimpleGrid 
            columns={{ base: 2, md: 4, lg: 4 }} 
            spacing={4} 
            width="100%"
            mb={4}
          >
            <Card 
              bg="gray.800" 
              borderWidth="1px" 
              borderColor="gray.700"
              boxShadow="lg"
              _hover={{ transform: "translateY(-2px)", boxShadow: "xl" }}
              transition="all 0.2s"
            >
              <CardBody p={4}>
                <Stat>
                  <StatLabel color="gray.400" fontSize="xs">
                    Total Stocks
                  </StatLabel>
                  <StatNumber color="purple.300" fontSize={{ base: "lg", md: "xl" }}>
                    {stocks.length}
                  </StatNumber>
                  <StatHelpText color="gray.500" fontSize="xs">
                    Companies
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card bg="gray.800" borderWidth="1px" borderColor="gray.700" boxShadow="lg"
              _hover={{ transform: "translateY(-2px)", boxShadow: "xl" }}
              transition="all 0.2s">
              <CardBody p={4}>
                <Stat>
                  <StatLabel color="gray.400" fontSize="xs">
                    Best Performer
                  </StatLabel>
                  <StatNumber color="green.300" fontSize={{ base: "lg", md: "xl" }}>
                    {bestStock?.ticker || 'N/A'}
                  </StatNumber>
                  <StatHelpText color="gray.500" fontSize="xs">
                    Top stock
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card 
              bg="gray.800" 
              borderWidth="1px" 
              borderColor="gray.700"
              boxShadow="lg"
              _hover={{ transform: "translateY(-2px)", boxShadow: "xl" }}
              transition="all 0.2s"
            >
              <CardBody p={4}>
                <Stat>
                  <StatLabel color="gray.400" fontSize="xs">
                    Income / Year
                  </StatLabel>
                  <StatNumber color="green.300" fontSize={{ base: "lg", md: "xl" }}>
                    ${totalDividends.toFixed(2)}
                  </StatNumber>
                  <StatHelpText color="gray.500" fontSize="xs">
                    Expected annually
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card 
              bg="gray.800" 
              borderWidth="1px" 
              borderColor="gray.700"
              boxShadow="lg"
              _hover={{ transform: "translateY(-2px)", boxShadow: "xl" }}
              transition="all 0.2s"
            >
              <CardBody p={4}>
                <Stat>
                  <StatLabel color="gray.400" fontSize="xs">
                    {/* ✅ Afișează din dividends dacă există, altfel din stocks.paid_div */}
                    {dividends.length > 0 ? "Dividends (After Tax)" : "Paid Dividends"}
                  </StatLabel>
                  <StatNumber color="yellow.300" fontSize={{ base: "lg", md: "xl" }}>
                    ${dividends.length > 0 ? netDividends.toFixed(2) : totalPaidDividends.toFixed(2)}
                  </StatNumber>
                  <StatHelpText color="gray.500" fontSize="xs">
                    Received so far
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>

          </SimpleGrid>

          {/* ✅ Tabs - arată dividends doar dacă există */}
          {dividends.length > 0 ? (
            <Box width="100%">
              <Tabs variant="soft-rounded" colorScheme="teal">
                <TabList mb={4} justifyContent="center">
                  <Tab fontSize="sm" fontWeight="semibold">
                    📊 Stocks ({stocks.length})
                  </Tab>
                  <Tab fontSize="sm" fontWeight="semibold">
                    💰 Dividends ({dividends.length})
                  </Tab>
                </TabList>

                <TabPanels>
                  {/* Tab 1: Stocks */}
                  <TabPanel p={0}>
                    <Flex justify="center" align="center" width="100%">
                      <Box
                        p={4}
                        borderWidth="1px"
                        borderColor="gray.700"
                        borderRadius="lg"
                        boxShadow="lg"
                        width="100%"
                        overflowX="auto"
                        bg="gray.800"
                      >
                        <Table variant="simple" size="sm" colorScheme="teal">
                          <Thead bg="gray.900">
                            <Tr>
                              <Th color="teal.200" fontSize="xs" textAlign="center" px={2} py={3}>
                                <Tooltip label="The name of the company you've invested in" hasArrow>
                                  Company
                                </Tooltip>
                              </Th>
                              <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Ticker</Th>
                              <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Shares</Th>
                              <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Price</Th>
                              <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Value</Th>
                              <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Avg/Share</Th>
                              <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Acquisition</Th>
                              <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Profit</Th>
                              <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Profit %</Th>
                              <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Div/Share</Th>
                              <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Income/Yr</Th>
                              <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Paid Div</Th>
                              <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>% Recover</Th>
                              <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Commission</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {stocks.map((st) => (
                              <Stock 
                                stock={st} 
                                key={st.id}
                                onPriceUpdate={(price) => {
                                  setCurrentPrices(prev => ({
                                    ...prev,
                                    [st.id]: price
                                  }));
                                }}
                              />
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    </Flex>
                  </TabPanel>

                  {/* Tab 2: Dividends */}
                  <TabPanel p={0}>
                    <Flex justify="center" align="center" width="100%">
                      <Box
                        p={4}
                        borderWidth="1px"
                        borderColor="gray.700"
                        borderRadius="lg"
                        boxShadow="lg"
                        width="100%"
                        overflowX="auto"
                        bg="gray.800"
                      >
                        {/* Summary Stats */}
                        <HStack justify="space-around" mb={4} flexWrap="wrap" spacing={4}>
                          <VStack spacing={0}>
                            <Text fontSize="xs" color="gray.500">Total Paid</Text>
                            <Text fontSize="lg" fontWeight="bold" color="green.300">
                              ${totalDividendsReceived.toFixed(2)}
                            </Text>
                          </VStack>
                          <VStack spacing={0}>
                            <Text fontSize="xs" color="gray.500">Total Tax</Text>
                            <Text fontSize="lg" fontWeight="bold" color="red.300">
                              ${totalTaxPaid.toFixed(2)}
                            </Text>
                          </VStack>
                          <VStack spacing={0}>
                            <Text fontSize="xs" color="gray.500">Net Amount</Text>
                            <Text fontSize="lg" fontWeight="bold" color="teal.300">
                              ${netDividends.toFixed(2)}
                            </Text>
                          </VStack>
                        </HStack>

                        <Table variant="simple" size="sm">
                          <Thead bg="gray.900">
                            <Tr>
                              <Th color="green.200" fontSize="xs" textAlign="center" px={2}>Date</Th>
                              <Th color="green.200" fontSize="xs" textAlign="center" px={2}>Ticker</Th>
                              <Th color="green.200" fontSize="xs" textAlign="center" px={2}>Description</Th>
                              <Th color="green.200" fontSize="xs" textAlign="center" px={2}>Amount</Th>
                              <Th color="green.200" fontSize="xs" textAlign="center" px={2}>Tax</Th>
                              <Th color="green.200" fontSize="xs" textAlign="center" px={2}>Net</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {dividends.map((dividend) => (
                              <DividendRow key={dividend.id} dividend={dividend} />
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    </Flex>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Box>
          ) : (
            /* ✅ Dacă nu există dividends, arată doar tabelul stocks (fără tabs) */
            <Flex justify="center" align="center" width="100%" p={1}>
              <Box
                p={4}
                borderWidth="1px"
                borderColor="gray.700"
                borderRadius="lg"
                boxShadow="lg"
                width="100%"
                overflowX="auto"
                bg="gray.800"
              >
                <Heading size="sm" mb={4} textAlign="center" color="teal.300">
                  📊 Stocks & Dividends Details
                </Heading>

                <Table variant="simple" size="sm" colorScheme="teal">
                  <Thead bg="gray.900">
                    <Tr>
                      <Th color="teal.200" fontSize="xs" textAlign="center" px={2} py={3}>
                        <Tooltip label="The name of the company you've invested in" hasArrow>
                          Company
                        </Tooltip>
                      </Th>
                      <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Ticker</Th>
                      <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Shares</Th>
                      <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Price</Th>
                      <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Value</Th>
                      <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Avg/Share</Th>
                      <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Acquisition</Th>
                      <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Profit</Th>
                      <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Profit %</Th>
                      <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Div/Share</Th>
                      <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Income/Yr</Th>
                      <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Paid Div</Th>
                      <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>% Recover</Th>
                      <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Commission</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {stocks.map((st) => (
                      <Stock 
                        stock={st} 
                        key={st.id}
                        onPriceUpdate={(price) => {
                          setCurrentPrices(prev => ({
                            ...prev,
                            [st.id]: price
                          }));
                        }}
                      />
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </Flex>
          )}
        </VStack>
      </div>
    </Page>
  );
};

export default Stocks;